<template>
    <b-overlay :show="loading" variant="white" :rounded="true">
        <template v-slot:overlay>
            <div class="text-center">
                <b-spinner
                    label="Carregando..."
                    style="width: 3rem; height: 3rem"
                />
            </div>
        </template>
        <b-card class="p-0 m-0 shadow-sm" body-class="p-2">
            <chart-toolbar :chart="chart" />
            <div style="position: relative; height: 85vh">
                <canvas ref="canvas" />
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import { Chart, defaultOptions, tickOptions } from "@/modules/chart.js";
import ChartToolbar from "@/modules/common/components/ChartToolbar";
import { PRISMS_Y_AXES } from "@/modules/campos_novos/settings";

export default {
    name: "PrismChart",
    components: {
        ChartToolbar
    },
    props: {
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            chart: null
        };
    },
    mounted() {
        this.chart = new Chart(this.$refs.canvas, {
            type: "line",
            data: {
                datasets: []
            },
            options: {
                ...defaultOptions,
                scales: {
                    xAxes: [
                        {
                            display: "auto",
                            type: "time",
                            time: {
                                tooltipFormat: "DD/MM/YYYY HH:mm",
                                displayFormats: {
                                    hour: "D MMM HH:mm",
                                    day: "D MMM"
                                },
                                minUnit: "hour"
                            },
                            ticks: {
                                maxTicksLimit: 9
                            }
                        }
                    ],
                    yAxes: [
                        {
                            type: "linear",
                            id: "left-axis",
                            display: "auto",
                            scaleLabel: {
                                display: true,
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        },
                        {
                            type: "linear",
                            id: "right-axis",
                            display: "auto",
                            position: "right",
                            scaleLabel: {
                                display: true,
                                labelString: "Cota (m)",
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        }
                    ]
                }
            }
        });
    },
    beforeDestroy() {
        this.chart?.destroy();
    },
    methods: {
        update({
            results,
            simulationResults,
            reservoirInfos,
            analysisVariable,
            requestedDataType
        }) {
            this.chart.resetZoom();

            let resultsDatasets = [];
            let simulationResultsDatasets = [];
            let reservoirInfoDatasets = [];

            const numResults = results.length;
            const numSimulationResults = simulationResults.length;
            const numReservoirInfos = reservoirInfos.length;
            const info = PRISMS_Y_AXES[analysisVariable];

            for (let i = 0; i < numResults; i++) {
                const { name, results: measures } = results[i];

                resultsDatasets.push({
                    label: `Medição ${name}`,
                    data: measures.map(item => ({
                        x: item.epoch,
                        y: item[analysisVariable] * info.rate
                    })),
                    yAxisID: "left-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0
                });
            }

            for (let i = 0; i < numSimulationResults; i++) {
                const { name, results: simulatedMeasures } = simulationResults[
                    i
                ];

                resultsDatasets.push({
                    label: `Simulação ${name}`,
                    data: simulatedMeasures.map(item => ({
                        x: item.epoch,
                        y: item[analysisVariable] * info.rate
                    })),
                    yAxisID: "left-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0
                });
            }

            if (numReservoirInfos > 0) {
                reservoirInfoDatasets.push({
                    label: "Montante",
                    data: reservoirInfos.map(item => ({
                        x: item.epoch,
                        y: item.quota_flux
                    })),
                    yAxisID: "right-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0
                });
            }

            this.chart.options.scales.yAxes[0].scaleLabel.labelString = `${info.label} (${info.unit})`;
            this.chart.data.datasets = [
                ...resultsDatasets,
                ...simulationResultsDatasets,
                ...reservoirInfoDatasets
            ];
            this.chart.update();
        }
    }
};
</script>
